import cn from "classnames";
import { useLocation } from "react-router-dom";
import { useEffect, useState, useRef } from "react";

// store
import { useResponsiveStore } from "store/useResponsiveStore";
import useAuthStore from "store/useAuthStore";

// constants
import { Permissions } from "constants/permissions";

// styles
import styles from "./SectionNavigationMenu.module.scss";

const InPageMenuItems = [
  {
    name: "Profile overview",
    href: "#profile-overview",
    visible: true
  },
  {
    name: "Organization & ownership",
    href: "#organization-ownership",
    visible: true
  },
  {
    name: "Size",
    href: "#size",
    visible: false
  },
  {
    name: "Certifications",
    href: "#certifications",
    visible: true
  },
  {
    name: "NAICS codes",
    href: "#naics-codes",
    visible: true
  },
  {
    name: "Service information",
    href: "#service-information",
    visible: true
  },
  {
    name: "Performance history",
    href: "#performance-history",
    visible: true
  },
  {
    name: "Export information",
    href: "#export-information",
    visible: true
  },
]

const LimitedProfileMenuItems = [
  {
    name: "Certifications",
    href: "#certifications",
    visible: true,
  },
];

export default function SectionNavigationMenu({
  isLimitedProfilePage,
}: {
  isLimitedProfilePage?: boolean;
}) {
  const { hash } = useLocation();
  const { closeMenu } = useResponsiveStore();
  const { user } = useAuthStore();

  const role = user?.role ?? "guest"
  const privileged = Permissions[role]?.privilegedSearch ?? false;

  const sanitizedMenuItems = InPageMenuItems.filter((item) => {
  if (privileged || item.visible) {
    return true;
  } else return false
});

  const menuItems = isLimitedProfilePage
    ? LimitedProfileMenuItems
    : sanitizedMenuItems;

  const [activeItem, setActiveItem] = useState(menuItems[0].href);
  const observer = useRef(null);

  useEffect(() => {
    setActiveItem(menuItems[0].href);
  }, [menuItems])

  useEffect(() => {
    if (hash) {
      // Scroll to section once the page has fully loaded
      window.onload = () => {
        const element = document.getElementById(hash.replace("#", ""));
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      };
    }
  }, [hash]);

  useEffect(() => {
    const sections = InPageMenuItems.map((item) =>
      document.getElementById(item.href.replace("#", ""))
    );

    // Observe sections and update the active menu item as the user scrolls.
    observer.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveItem(`#${entry.target.id}`);
          }
        });
      },
      { rootMargin: "0px 0px -80% 0px", threshold: 0.1 }
    );

    sections.forEach((section) => {
      if (section) observer.current.observe(section);
    });

    // Disconnects the observer on component unmount to avoid memory leaks.
    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, []);

  return (
    <aside
      className={cn(styles["section-navigation-menu"], "usa-in-page-nav")}
      data-title-text="On this page"
      data-title-heading-level="h4"
      data-scroll-offset="2rem"
      data-root-margin="0px 0px 0px 0px"
      data-threshold="1"
    >
      <p className="usa-in-page-nav__heading">On this page</p>
      <ul className="usa-in-page-nav__list">
        {menuItems.map((item) => (
          <li className="usa-in-page-nav__item" key={item.name}>
            <a
              href={item.href}
              onClick={closeMenu}
              className={cn(
                "usa-link usa-in-page-nav__link ",
                {
                  "usa-current": activeItem === item.href,
                },
                styles["item"]
              )}
            >
              {item.name}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}
